import { Schema, SchemaField, DocumentExtraction, SchemaExtractionData, ExtractedDataField } from '../../../types/dataTypes';
import { supabase } from '../../../core/lib/supabase';

/**
 * Interface for extraction response
 */
export interface ExtractionResult {
  schema: string;
  extracted_data: Record<string, any>;
  document_types: string[];
  token_count: number;
}

/**
 * Interface for template changes response
 */
export interface TemplateChangesResult {
  success: boolean;
  changes: Array<[number, string, number]>; // [operation, text, position]
}

// Interface for workpill schema query result
interface WorkpillSchemaResult {
  id: string;
  schema_id: string;
  name: string | null;
  created_at: string;
  schemas: {
    id: string;
    name: string;
    description: string;
    created_by: string;
    created_at: string;
    schema_fields: {
      id: string;
      name: string;
      type: string;
      schema_id: string;
      created_at: string;
      description: string;
      is_extracted_automatically: boolean;
    }[];
  }[];
}

/**
 * Get workpill_id from execution_id
 */
async function getWorkpillIdFromExecution(executionId: string): Promise<string | null> {
  try {
    const { data: execution, error } = await supabase
      .from('workpill_executions')
      .select('workpill_id')
      .eq('id', executionId)
      .single();

    if (error) {
      console.error('Error fetching workpill_id for execution:', error);
      return null;
    }

    return execution.workpill_id;
  } catch (error) {
    console.error('Error getting workpill_id:', error);
    return null;
  }
}

/**
 * Get schemas associated with a workpill
 */
async function getWorkpillSchemas(workpillId: string, userId?: string): Promise<Schema[]> {
  try {
    const { data: workpillSchemas, error: schemasError } = await supabase
      .from('workpill_schemas')
      .select(`
        id,
        schema_id,
        name,
        created_at,
        schemas (
          id,
          name,
          description,
          created_by,
          created_at,
          schema_fields (
            id,
            name,
            type,
            schema_id,
            created_at,
            description,
            is_extracted_automatically
          )
        )
      `)
      .eq('workpill_id', workpillId);

    if (schemasError) {
      console.error('Error fetching workpill schemas:', schemasError);
      throw schemasError;
    }

    // Transform the API response to our expected format
    return workpillSchemas.map((item: WorkpillSchemaResult) => {
      if (!item.schemas || item.schemas.length === 0) {
        return {
          id: item.id,
          created_by: userId || '',
          name: 'Unknown Schema',
          description: '',
          created_at: item.created_at,
          fields: [],
          original_schema_id: item.schema_id,
          workpill_schema_name: item.name
        };
      }

      const schema = item.schemas[0];
      return {
        id: item.id, // workpill_schema id
        created_by: schema?.created_by || userId || '',
        name: schema?.name || 'Unknown Schema',
        description: schema?.description || '',
        created_at: item.created_at,
        fields: schema?.schema_fields?.map(field => ({
          id: field.id,
          schema_id: field.schema_id,
          name: field.name,
          type: field.type,
          description: field.description,
          created_at: field.created_at,
          is_extracted_automatically: field.is_extracted_automatically
        })) || [],
        original_schema_id: item.schema_id,
        workpill_schema_name: item.name
      };
    });
  } catch (error) {
    console.error('Error fetching workpill schemas:', error);
    return [];
  }
}

/**
 * Fetch latest extraction per schema for a given execution
 */
export async function fetchExecutionDocumentsBySchema(executionId: string): Promise<DocumentExtraction[]> {
  try {
    // First get the workpill_id from the execution
    const { data: execution, error: executionError } = await supabase
      .from('workpill_executions')
      .select('workpill_id')
      .eq('id', executionId)
      .single();

    if (executionError) {
      console.error('Error fetching workpill_id for execution:', executionError);
      throw executionError;
    }

    const workpillId = execution.workpill_id;
    console.log(`Found workpill_id: ${workpillId} for execution: ${executionId}`);

    // Now get the schemas associated with this workpill
    const { data: workpillSchemas, error: schemasError } = await supabase
      .from('workpill_schemas')
      .select(`
        id,
        schema_id,
        name,
        schemas (
          id,
          name
        )
      `)
      .eq('workpill_id', workpillId);

    if (schemasError) {
      console.error('Error fetching workpill schemas:', schemasError);
      throw schemasError;
    }

    // Create a list of workpill_schema entries
    const workpillSchemaEntries = workpillSchemas.map((ws: any) => ({
      id: ws.id,
      schema_id: ws.schema_id,
      name: ws.name || ws.schemas[0]?.name || 'Unknown Schema'
    }));

    if (workpillSchemaEntries.length === 0) {
      console.log('No schemas found for this workpill');
      return [];
    }

    // For each workpill_schema entry, find the latest document extraction
    const documentExtractions: DocumentExtraction[] = await Promise.all(
      workpillSchemaEntries.map(async (wsEntry) => {
        // First find the latest document for this execution that uses this workpill_schema
        const { data: latestDocInfo, error: docInfoError } = await supabase
          .from('workpill_execution_documents')
          .select('id, created_at')
          .eq('execution_id', executionId)
          .eq('workpill_schema_id', wsEntry.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (docInfoError || !latestDocInfo) {
          console.log(`No document found for workpill_schema ${wsEntry.id} in execution ${executionId}`);
          // Return an empty extraction for this schema
          return {
            id: wsEntry.id,
            documentId: '',
            workpillSchemaId: wsEntry.id,
            status: 'empty' as const,
            extractedDate: new Date(),
            fields: []
          };
        }

        // Now get the extracted data for this document
        const { data: extractedData, error: extractedDataError } = await supabase
          .from('workpill_execution_document_extracted_data')
          .select('extracted_data, created_at')
          .eq('workpill_execution_document_id', latestDocInfo.id)
          .eq('workpill_schema_id', wsEntry.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        // If we have document but no extracted data or there was an error
        if (extractedDataError || !extractedData) {
          console.log(`No extracted data found for document ${latestDocInfo.id} (schema ${wsEntry.schema_id})`);
          return {
            id: wsEntry.id,
            documentId: latestDocInfo.id,
            workpillSchemaId: wsEntry.id,
            status: 'empty' as const,
            extractedDate: new Date(latestDocInfo.created_at),
            fields: []
          };
        }

        // Get document ID
        const documentId = latestDocInfo.id;

        // Construct the storage path based on document ID
        const storagePath = `${documentId}/document.pdf`;
        
        // Get a signed URL for the document
        const { data: urlData } = await supabase.storage
          .from('workpill-assignation-documents')
          .createSignedUrl(storagePath, 3600);

        // Convert extracted data to fields format
        console.log('📊 Raw extracted_data:', extractedData.extracted_data);
        const fields = extractedData.extracted_data ? Object.entries(extractedData.extracted_data).map(([key, value]) => {
          // Check if there are validations stored
          const validations = extractedData.extracted_data._validations || {};
          const field = {
            name: key,
            value: String(value),
            validation: { 
              isValid: validations[key] || null,
              correctedValue: undefined
            }
          };
          console.log(`📊 Created field:`, field);
          return field;
        }).filter(field => field.name !== '_validations') : []; // Filter out the _validations key
        
        console.log('📊 Final fields array:', fields);

        return {
          id: wsEntry.id,
          documentId: documentId,
          workpillSchemaId: wsEntry.id,
          status: fields.length > 0 ? 'completed' as const : 'empty' as const,
          extractedDate: new Date(extractedData.created_at),
          documentUrl: urlData?.signedUrl || undefined,
          fields
        };
      })
    );

    return documentExtractions;

  } catch (error) {
    console.error('Error in fetchExecutionDocumentsBySchema:', error);
    return [];
  }
}

/**
 * Transform data from API format to our component format
 */
function transformToSchemaExtractionData(
  schemas: Schema[], 
  extractions: DocumentExtraction[]
): SchemaExtractionData[] {
  console.log('🔄 Transforming data:');
  console.log('📋 Schemas:', schemas);
  console.log('📄 Extractions:', extractions);
  
  return schemas.map(schema => {
    console.log(`🔍 Processing schema: ${schema.id} (${schema.name})`);
    
    // Find the extraction for this schema
    const extraction = extractions.find(ext => ext.workpillSchemaId === schema.id);
    console.log(`📄 Found extraction for schema ${schema.id}:`, extraction);
    
    // Transform schema fields to extracted data fields
    const extractedFields: ExtractedDataField[] = [];
    
    if (schema.fields && schema.fields.length > 0) {
      // Map schema fields with their extracted values
      schema.fields.forEach(field => {
        console.log(`🔍 Processing field: ${field.name}`);
        const extractedField = extraction?.fields.find(ef => ef.name === field.name);
        console.log(`📄 Found extracted field for ${field.name}:`, extractedField);
        
        extractedFields.push({
          id: field.id,
          name: field.name,
          value: extractedField?.value || '',
          type: field.type,
          isValidated: extractedField?.validation.isValid || null,
          correctedValue: extractedField?.validation.correctedValue
        });
      });
    } else if (extraction && extraction.fields && extraction.fields.length > 0) {
      // If no schema fields, create fields from extraction data directly
      console.log('⚠️ No schema fields found, creating from extraction data');
      extraction.fields.forEach((extractedField, index) => {
        extractedFields.push({
          id: `extracted_${index}`,
          name: extractedField.name,
          value: extractedField.value,
          type: 'text',
          isValidated: extractedField.validation.isValid || null,
          correctedValue: extractedField.validation.correctedValue
        });
      });
    }

    console.log(`✅ Final extracted fields for schema ${schema.id}:`, extractedFields);

    return {
      id: schema.id,
      name: schema.workpill_schema_name || schema.name,
      description: schema.description,
      uploadDate: new Date(schema.created_at),
      status: extraction ? extraction.status : 'empty',
      schemaId: schema.original_schema_id || schema.id,
      extractedFields,
      documentUrl: extraction?.documentUrl,
      documentId: extraction?.documentId
    };
  });
}

/**
 * Main function to get schema extraction data for an execution
 */
export async function getSchemaExtractionData(executionId: string, userId?: string): Promise<SchemaExtractionData[]> {
  try {
    // 1. Get workpill_id from execution
    const workpillId = await getWorkpillIdFromExecution(executionId);
    if (!workpillId) {
      console.error('Could not find workpill_id for execution:', executionId);
      return [];
    }

    // 2. Get schemas for the workpill
    const schemas = await getWorkpillSchemas(workpillId, userId);
    if (schemas.length === 0) {
      console.log('No schemas found for workpill:', workpillId);
      return [];
    }

    // 3. Get extraction data for the execution
    const extractions = await fetchExecutionDocumentsBySchema(executionId);

    // 4. Transform and combine the data
    const schemaExtractionData = transformToSchemaExtractionData(schemas, extractions);

    console.log(`Successfully retrieved ${schemaExtractionData.length} schema extractions`);
    return schemaExtractionData;

  } catch (error) {
    console.error('Error in getSchemaExtractionData:', error);
    return [];
  }
}

/**
 * Update extracted field value
 */
export async function updateExtractedFieldValue(
  documentId: string,
  workpillSchemaId: string,
  fieldName: string,
  newValue: string
): Promise<void> {
  try {
    // First get the current extracted data
    const { data: currentData, error: fetchError } = await supabase
      .from('workpill_execution_document_extracted_data')
      .select('extracted_data')
      .eq('workpill_execution_document_id', documentId)
      .eq('workpill_schema_id', workpillSchemaId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (fetchError) {
      console.error('Error fetching current extracted data:', fetchError);
      throw fetchError;
    }

    if (!currentData) {
      throw new Error('No extracted data found for the given document and schema');
    }

    // Update the specific field in the extracted data
    const updatedData = {
      ...currentData.extracted_data,
      [fieldName]: newValue
    };

    // Update the record in the database
    const { error: updateError } = await supabase
      .from('workpill_execution_document_extracted_data')
      .update({ extracted_data: updatedData })
      .eq('workpill_execution_document_id', documentId)
      .eq('workpill_schema_id', workpillSchemaId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (updateError) {
      console.error('Error updating extracted data:', updateError);
      throw updateError;
    }
  } catch (error) {
    console.error('Error in updateExtractedFieldValue:', error);
    throw error;
  }
}

/**
 * Update field validation status
 */
export async function updateFieldValidationStatus(
  documentId: string,
  workpillSchemaId: string,
  fieldName: string,
  isValid: boolean | null
): Promise<void> {
  try {
    // First get the current extracted data
    const { data: currentData, error: fetchError } = await supabase
      .from('workpill_execution_document_extracted_data')
      .select('extracted_data, id')
      .eq('workpill_execution_document_id', documentId)
      .eq('workpill_schema_id', workpillSchemaId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (fetchError) {
      console.error('Error fetching extracted data for validation update:', fetchError);
      throw fetchError;
    }

    if (!currentData) {
      throw new Error('No extracted data found for the given document and schema');
    }

    // Get current validations or create a new validations object
    let validations = currentData.extracted_data._validations || {};

    // Update the validation for this field
    validations = {
      ...validations,
      [fieldName]: isValid
    };

    // Update the extracted data with the new validations
    const updatedData = {
      ...currentData.extracted_data,
      _validations: validations
    };

    // Update the record in the database
    const { error: updateError } = await supabase
      .from('workpill_execution_document_extracted_data')
      .update({ extracted_data: updatedData })
      .eq('id', currentData.id);

    if (updateError) {
      console.error('Error updating validation status:', updateError);
      throw updateError;
    }

    console.log(`Validation for field ${fieldName} updated to ${isValid}`);
  } catch (error) {
    console.error('Error in updateFieldValidationStatus:', error);
    throw error;
  }
}



